import { swivuRecentRecordSortFn } from "@/modern/utils";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys";
import { TASK_BUDGET_FIELDS, VIEW_TYPES } from "@/other/constants/OtherKeys";
import { SWIVU_STANDARD_ICONS } from "@/other/constants/IconKeys.jsx";
import type { TaskBudget } from "@/bryntum-lib/models/TaskBudget";
import { EntityFinderHighlight } from "@/modern/components/entity-finder/components/EntityFinderHighlight";
import { EntityFinderItemRow, entityFinderTintText } from "@/modern/components/entity-finder/components/EntityFinderItemRow";
import { EntityFinderMetaBadge } from "@/modern/components/entity-finder/components/EntityFinderMetaBadge";
import { renderEntityFinderConfigIcon } from "@/modern/components/entity-finder/components/renderEntityFinderConfigIcon";
import { estimateDefaultRowHeight, canCreateBudget, openEntityEditor } from "@/modern/components/entity-finder/constants/config/shared";
import type { EntityFinderConfigEntry } from "@/modern/components/entity-finder/types";
import {
  defaultEntityListSection,
  defaultMatchesScope,
  matchesSearch,
  passesArchiveFilter,
  searchModeActive,
} from "@/modern/components/entity-finder/utils";

function formatCurrency(value: unknown): string | null {
  const num = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(num) || num === 0) return null;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(num);
}

function mapBudget(budget: TaskBudget) {
  return {
    id: `budget-${budget.id}`,
    type: "budget" as const,
    label: (budget[TASK_BUDGET_FIELDS.title] as string) || "N/A",
    subtitle: (budget[TASK_BUDGET_FIELDS.description] as string) || undefined,
    record: budget,
  };
}

export const budgetConfig: EntityFinderConfigEntry<"budget"> = {
  type: "budget",
  label: "Budget",
  plural: "Budgets",
  icon: <i className={SWIVU_STANDARD_ICONS[VIEW_TYPES.taskBudget]} aria-hidden />,
  tint: "bg-chart-4/20 text-chart-4",
  listOrder: 60,
  showInFilter: true,
  isActive: searchModeActive,
  matchesScope: (ctx) => defaultMatchesScope(ctx, "budget"),
  getData: (ctx) => {
    const searchLower = ctx.debouncedQuery.toLowerCase();
    const records = swivuRecentRecordSortFn(
      ctx.project.taskBudgetStore.query(
        (budget: TaskBudget) =>
          passesArchiveFilter(budget.archived, ctx.isShowArchive) &&
          !budget.isLinked &&
          (matchesSearch(budget[TASK_BUDGET_FIELDS.title], searchLower) ||
            matchesSearch(budget[TASK_BUDGET_FIELDS.description], searchLower)),
        true
      ) as unknown as TaskBudget[],
      {
        currentBoardUserId: ctx.currentBoardUserId,
        nameField: TASK_BUDGET_FIELDS.title,
      }
    );
    return records.map(mapBudget);
  },
  getRecentItem: (ctx) => {
    const records = swivuRecentRecordSortFn(
      ctx.project.taskBudgetStore.query(
        (b: TaskBudget) => passesArchiveFilter(b.archived, ctx.isShowArchive) && !b.isLinked,
        true
      ) as unknown as TaskBudget[],
      { currentBoardUserId: ctx.currentBoardUserId, nameField: TASK_BUDGET_FIELDS.title }
    ).slice(0, 1);
    return records[0] ? mapBudget(records[0]) : null;
  },
  buildListSection: (items, _ctx) => defaultEntityListSection(budgetConfig, items),
  estimateRowHeight: estimateDefaultRowHeight,
  getMetadata: (item) => {
    const budget = item.record as TaskBudget;
    const amount =
      formatCurrency(budget[TASK_BUDGET_FIELDS.contractAmount]) ??
      formatCurrency(budget[TASK_BUDGET_FIELDS.totalContract]);
    return amount ? { text: amount, variant: "amount" } : null;
  },
  itemRenderer: ({ item, query, ctx, metadata, entry }) => {
    const badge = metadata ?? budgetConfig.getMetadata?.(item, ctx);

    return (
      <EntityFinderItemRow
        icon={renderEntityFinderConfigIcon(entry.icon, 14, "text-sm")}
        iconClassName={entityFinderTintText(budgetConfig.tint)}
        title={<EntityFinderHighlight text={item.label} query={query} />}
        subtitle={
          item.subtitle ? (
            <EntityFinderHighlight text={item.subtitle} query={query} tone="muted" />
          ) : undefined
        }
        trailing={
          badge ? (
            <EntityFinderMetaBadge variant="success" title={badge.text}>
              {badge.text}
            </EntityFinderMetaBadge>
          ) : undefined
        }
      />
    );
  },
  onSelect: (item, ctx) => {
    openEntityEditor(ctx, EDITOR_KEYS.budgetEditor, item.record, { budget: item.record }, {
      by: "recordType",
      useExisting: true,
    });
  },
  createOnUnmatched: {
    label: "Create new budget",
    fn: (query, ctx) => {
      const trimmed = query.trim();
      if (!trimmed) return;
      const newRecord = (
        ctx.project.taskBudgetStore as { createRecord: (data?: object) => unknown }
      ).createRecord({ [TASK_BUDGET_FIELDS.title]: trimmed });
      openEntityEditor(ctx, EDITOR_KEYS.budgetEditor, newRecord, { budget: newRecord });
    },
    isEnabled: canCreateBudget,
  },
};
